import { useState, forwardRef, useId, useEffect, useRef } from "react";
import { FormControl, FormGroup } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { SearchIcon, EyeIcon, SlashEyeIcon } from "../../icons/inputIcons";
import { FormLabel, FormFeedback } from "./utils";
import DateTimePicker from "./DateTimePicker";
import { CalendarTimeIcon } from "../../icons/otherIcons";
import FormRange from "react-bootstrap/esm/FormRange";

const Input = forwardRef(
  (
    {
      icon = null,
      startIcon = false,
      className = "",
      sortable = false,
      coverClass = "",
      isHorizontal = false,

      onKeyDown,
      style = {},
      input = {},
      label = {},
      validation = {},
      iconWidth = 16,
      required = false,
      iconClass = "",
    },

    ref,
  ) => {
    const {
      placeholder = "",
      value = "",
      onChange = () => { },
      onBlur = () => { },
      readOnly = false,
      name = "",
      type = "text",
      step,
      className: inputClassName = "py-6",
      as,
      id: inputId,
      rows,

      ...restInput
    } = input || {};

    const {
      name: labelName = "",
      id: labelId,
      isHidden = false,
      class: labelClass = "",
    } = label || {};
    const {
      isInvalid = false,
      message: errorMessage = "",
      className: validationClass = "",
    } = validation || {};

    // ----------------------------------------------
    // ID Handling — robust & always valid
    // ----------------------------------------------
    const autoId = useId();
    const resolvedId = labelId || inputId || autoId;
    // ----------------------------------------------
    // Password toggle
    // ----------------------------------------------
    const [showPassword, setShowPassword] = useState(false);
    const isPasswordType = type === "password";
    const effectiveType = isPasswordType
      ? showPassword
        ? "text"
        : "password"
      : type;
    // ----------------------------------------------
    // Phone Input state
    // ----------------------------------------------
    const [internalPhoneValue, setInternalPhoneValue] = useState(value || "91");
    const isFirstRender = useRef(true);

    useEffect(() => {
      if (type !== 'phone') return;
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      if (value) {
        setInternalPhoneValue(value);
      } else if (value === "") {
        setInternalPhoneValue("91");
      }
    }, [value, type]);

    // ----------------------------------------------
    // Conditions
    // ----------------------------------------------
    const hasIcon = isPasswordType || icon || (type === "number" && sortable);
    const isDateTimeLocal = type === "datetime-local" && as !== "textarea";

    return (
      <FormGroup
        className={`form-input-cover  ${isHorizontal
          ? `grid align-items-center ${isInvalid ? "mb-3" : ""}`
          : ""
          } ${className}`.trim()}
        style={style}
      >
        {/* Label */}

        <FormLabel
          className={`text-primary ${isHorizontal ? "g-col-4 m-0" : ""} ${isHidden ? "visually-hidden" : ""
            } ${labelClass}`.trim()}
          id={resolvedId}
          labelName={labelName}
          required={required}
        />

        {/* INPUT + ICONS */}
        <div
          className={`${isHorizontal ? "g-col-8" : ""
            }  position-relative ${coverClass}  ${isInvalid && errorMessage ? "mb-3" : ""
            }`.trim()}
        >
          <div className={`position-relative ${type === "range" ? "d-flex align-items-center gap-4" : ""}`}>
            {type === "phone" ? (
              <PhoneInput
                country={"in"}
                enableSearch={true}
                value={internalPhoneValue}
                onChange={(val, country, e, formattedValue) => {
                  setInternalPhoneValue(val);
                  if (!val || val === country?.dialCode) {
                    onChange("");
                  } else {
                    onChange(formattedValue);
                  }
                }}
                onBlur={onBlur}
                disabled={restInput.disabled || readOnly}
                inputProps={{
                  name: name,
                  id: resolvedId,
                  required: required,
                  autoComplete: 'new-password'
                }}
                inputStyle={{ height: "auto", lineHeight: "1.2" }}
                buttonStyle={{ height: "auto", bottom: "0px", top: "0px", left: "0px", borderColor: "var(--aim-primary-border-subtle, #dee2e6)" }}
                containerClass={`react-phone-input-container ${isInvalid ? "is-invalid" : ""}`}
                inputClass={`form-control rounded-4 w-100 ${inputClassName} ${
                  isInvalid ? "is-invalid border-danger" : ""
                }`}
                buttonClass={`rounded-start-4 ${isInvalid ? "border-danger" : ""}`}
                {...restInput}
              />
            ) : isDateTimeLocal ? (
              <DateTimePicker
                value={value}
                onChange={onChange}
                name={name}
                placeholder={placeholder}
                className={`form-control rounded-4  ${inputClassName} pe-17 ${isInvalid ? "is-invalid" : ""}`.trim()}
                {...restInput}
              />
            ) : type === "range" ? (<>
              <FormRange value={value ?? ""} onChange={onChange} step={step} name={name}  {...restInput} />
              <span className="fw-semibold text-success fs-sm">
                {value || 0}%
              </span>
            </>

            ) : (
              <FormControl
                ref={ref}
                type={as === "textarea" ? undefined : effectiveType}
                id={resolvedId}
                name={name}
                placeholder={placeholder}
                readOnly={readOnly}
                value={value ?? ""}
                onChange={onChange}
                onBlur={onBlur}
                onWheel={(e) => {
                  if (type === "number") {
                    e.target.blur();
                  }
                }}
                onKeyDown={(e) => {
                  if (type === "number" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
                    e.preventDefault();
                  }
                  if (onKeyDown) onKeyDown(e);
                }}
                // required={required}
                isInvalid={!!isInvalid}
                aria-invalid={!!isInvalid}
                step={type === "number" ? step || "any" : undefined}
                autoComplete="off"
                as={as}
                rows={rows}
                className={`rounded-4  ${inputClassName} ${(hasIcon && startIcon) || type === "search"
                  ? "ps-16"
                  : hasIcon
                    ? "pe-16"
                    : ""
                  } ${isInvalid ? "is-invalid" : ""}`.trim()}
                {...restInput}
              />
            )}

            {/* Password toggle */}
            {isDateTimeLocal ? <i
              className={`ratio-1 flex-center position-absolute top-50 translate-middle-y end-0 me-6 ${iconClass}`}
              style={{ width: iconWidth }}
              aria-label={"Select Date and Time"}
            >
              <CalendarTimeIcon size={iconWidth} />
            </i> : isPasswordType ? (
              <i
                className={`ratio-1 flex-center position-absolute top-50 translate-middle-y end-0 me-8 cursor-pointer text-primary`}
                onClick={() => setShowPassword((s) => !s)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setShowPassword((s) => !s);
                  }
                }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <SlashEyeIcon size={18} />
                ) : (
                  <EyeIcon size={18} />
                )}
              </i>
            ) : type === "search" ? (
              <>
                <i
                  className={`ratio-1 flex-center position-absolute top-50 translate-middle-y start-0 ms-6 ${iconClass}`}
                  style={{ width: iconWidth }}
                >
                  <SearchIcon size={iconWidth} />
                </i>
              </>
            ) : (
              <>
                {/* Custom icon */}
                {icon && (
                  <i
                    className={`ratio-1 flex-center position-absolute top-50 translate-middle-y  ${startIcon ? "start-0 ms-6" : "end-0 me-6"
                      }`}
                    style={{ width: iconWidth }}
                  >
                    {icon}
                  </i>
                )}
              </>
            )}
          </div>
          {/* ERROR MESSAGE */}
          {isInvalid && errorMessage && (
            <FormFeedback
              className={`position-absolute top-100 mt-1 ${validationClass}`}
              message={errorMessage}
            />
          )}
        </div>
      </FormGroup>
    );
  },
);

Input.displayName = "Input";
export default Input;
